import { format } from "date-fns";
import { useState } from "react";

import { Card } from "@/components/card";
import { useTableControl } from "@/components/table-control";
import { Badge } from "@/components/ui/badge";

import { useGetMostActiveUsers } from "../api/get-most-active-users";
import { Timeframe } from "../types/timeframe";
import { generateTimeRange } from "../utils/generate-time-range";

import { MostActiveUsersTable } from "./most-active-users-table";
import { TimeframeSelector } from "./timeframe-selector";

type MostActiveUsersProps = {
  organisationId?: string;
};

export function MostActiveUsers({ organisationId }: MostActiveUsersProps) {
  const [timeFrame, setTimeFrame] = useState<Timeframe>("w");
  const { pagination, setPagination } = useTableControl({
    initialPageSize: 10,
    persistInUrl: false,
  });

  const { from, to } = generateTimeRange(timeFrame);
  const {
    data: mostActiveUsers,
    isSuccess,
    isLoading,
    isFetching,
  } = useGetMostActiveUsers({
    from: from.toISOString(),
    to: to.toISOString(),
    organisationId,
    limit: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
  });

  const pageCount = mostActiveUsers
    ? Math.ceil(mostActiveUsers.count / pagination.pageSize)
    : 0;

  return (
    <Card>
      <div className="flex items-center justify-between">
        <Card.Header
          title={
            <span>
              Most Active Users
              <Badge variant="outline" className="ml-1 font-normal">
                {format(from, "MMM dd")} - {format(to, "MMM dd")}
              </Badge>
            </span>
          }
        />
        <TimeframeSelector
          value={timeFrame}
          onChange={(value) => {
            setTimeFrame(value as Timeframe);
            setPagination((prev) => ({
              ...prev,
              pageIndex: 0,
            }));
          }}
        />
      </div>

      <div className="relative">
        <MostActiveUsersTable
          data={isSuccess ? mostActiveUsers?.items || [] : []}
          pagination={pagination}
          onPaginationChange={setPagination}
          pageCount={pageCount}
          isLoading={isLoading || isFetching}
        />
      </div>
    </Card>
  );
}
